import React, { useState, createContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

export const AdminContext = createContext({
  selectedUser: {},
  setSelectedUser: () => { },
  selectedRole: {},
  setSelectedRole: () => { },
  selectedPolicy: {},
  setSelectedPolicy: () => { },
  selectedField: {},
  setFilteredUsers: () => { },
  isLoading: false,
  setIsLoading: () => { },
  setFilteredRoles: () => { },
  setFilteredPolicies: () => { },
  setFilteredFields: () => { },
  setFilteredRepositories: () => { },
  siteOptions: {},
  setSiteOptions: () => { },
  searchFieldValues: {},
  setSearchFieldValues: () => { }
});

const DEFAULT_USER = {
  id: 0,
  name: '',
  email: '',
  username: '',
  phone: '',
  isAdmin: false,
  isInactive: false,
  isManager: false,
  isManagerOfUsers: false,
  changePassword: false,
  disableMFA: false,
  userRoles: [],
  isDisplayDocUser: false,
  ssoOnly: false
};

const DEFAULT_ROLE = {
  id: 0,
  name: '',
  searchFilter: '',
  canDownload: false,
  canEdit: false,
  canEmail: false,
  canUpload: false,
  canDelete: false,
  canModify: false,
  canSplit: false,
  roleUsers: [],
  roleRepositories: []
};

const DEFAULT_POLICY = {
  id: 0,
  name: '',
  isActive: true,
  scope: 'Documents',
  action: 'Notify',
  filter: '',
  nextRun: moment(Date()),
  frequency: '$Day(1)',
  notify: 'EmailText',
  expiration: '$Day(7)',
  policyUsers: [],
  policyRepositories: []
};

const DEFAULT_SITE_OPTIONS = {
  passwordRules: [],
  allowedFileExtensions: ['pdf'],
  maxHits: 500,
  maxExportHits: 5000,
  maxAuditHits: 500,
  maxAuditExportHits: 5000,
  enableMFA: false,
  mFACodeLength: 6,
  useGenericPwdMessage: false,
  nameAndPasswordProtectPDF: false,
  allowManagersOfUsersToViewAudits: false,
  enforcePasswordRulesOnDownloadAndEmail: false,
  forceEmailToUsePassword: false
};

const AdminContextProvider = ({ children }) => {
  const [selectedUser, setSelectedUser] = useState(DEFAULT_USER);
  const [selectedRole, setSelectedRole] = useState(DEFAULT_ROLE);
  const [selectedPolicy, setSelectedPolicy] = useState(DEFAULT_POLICY);
  const [siteOptions, setSiteOptions] = useState(DEFAULT_SITE_OPTIONS);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [filteredFields, setFilteredFields] = useState([]);
  const [filteredRepositories, setFilteredRepositories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFieldValues, setSearchFieldValues] = useState([]);

  const setSelectedUserToDefault = () => setSelectedUser(DEFAULT_USER);
  const setSelectedRoleToDefault = () => setSelectedRole(DEFAULT_ROLE);
  const setSelectedPolicyToDefault = () => setSelectedPolicy(DEFAULT_POLICY);

  return (
    <AdminContext.Provider
      value={{
        selectedUser,
        setSelectedUser,
        selectedRole,
        setSelectedRole,
        selectedPolicy,
        setSelectedPolicy,
        filteredUsers,
        setFilteredUsers,
        filteredRoles,
        setFilteredRoles,
        filteredPolicies,
        setFilteredPolicies,
        setFilteredFields,
        filteredFields,
        filteredRepositories,
        setFilteredRepositories,
        isLoading,
        setIsLoading,
        setSelectedUserToDefault,
        setSelectedRoleToDefault,
        setSelectedPolicyToDefault,
        siteOptions,
        setSiteOptions,
        searchFieldValues,
        setSearchFieldValues,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

AdminContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default AdminContextProvider;
